import { IconName } from "@pathwright/pathicons"
import { capitalCase } from "change-case"
import {
  CompletionFragment,
  CompletionsItemFragment
} from "@pathwright/graphql-client/src/generated"
import { getCompletionAction } from "../../../completion/utils"
import { getStepVerbIcon } from "../../../path/utils"
import { UseReviewableCompletionsReturn } from "../ReviewContextProvider"

export type TreeNodeType = "root" | "path" | "lesson" | "step" | "completion"

// Allows for typing the tree node id starting at node and going up the tree
// to the root.
type TreeNodeId<T extends TreeNodeType> = TreeNodeParent<T> extends null
  ? `${T}:${string | number}`
  : TreeNodeParent<T> extends TreeNode<infer P>
  ? `${TreeNodeId<P>}-${T}:${string | number}`
  : never

export type TreeNodeAnyId = TreeNodeId<TreeNodeType>

type TreeNodeAllowedRelations = {
  root: { parent: never; children: "path" }
  path: { parent: "root"; children: "lesson" }
  lesson: { parent: "path"; children: "step" }
  step: { parent: "lesson"; children: "completion" }
  completion: { parent: "step"; children: never }
}

export type TreeNodeParent<T extends TreeNodeType> =
  TreeNodeAllowedRelations[T]["parent"] extends never
    ? null
    : TreeNode<TreeNodeAllowedRelations[T]["parent"]>

type TreeNodeChildren<T extends TreeNodeType> =
  TreeNodeAllowedRelations[T]["children"] extends never
    ? null
    : Map<TreeNodeId<T>, TreeNode<TreeNodeAllowedRelations[T]["children"]>>

export type TreeNodeMetaTag =
  | "pending-review"
  | "overdue"
  | "reviewed"
  | "completed"
  | "incomplete"

// type TreeNodeId = string | number

export type TreeNode<T extends TreeNodeType> = {
  id: TreeNodeId<T>
  type: T
  meta: {
    title: string
    subtitle?: string | null
    image?: string | null
    icon?: IconName | null
  }
  data: T extends "root" ? null : CompletionsItemFragment
  parent: TreeNodeParent<T>
  children: TreeNodeChildren<T>
} & (
  | {
      type: "root" | "path" | "lesson" | "step"
    }
  | {
      type: "completion"
      user?: CompletionFragment["user"] | null
      created?: Date | null
      meta: {
        avatar?: string | null
        icon?: string | null
        image?: string | null
        when?: Date | null
        tags?: TreeNodeMetaTag[]
      }
    }
)

export type TreeNodeMap<T extends TreeNodeType> = Map<
  TreeNodeId<T>,
  TreeNode<T>
>

// Exhaustive union of all tree node types since TreeNode<any> can be infinitely deeeeep.
export type TreeNodeAny =
  | TreeNode<"root">
  | TreeNode<"path">
  | TreeNode<"lesson">
  | TreeNode<"step">
  | TreeNode<"completion">

// The actual nodes that can have children (i.e., not "completion" nodes).
export type TreeNodeParentNode = Exclude<TreeNodeAny, TreeNode<"completion">>

type GetNodeFromItem<T extends TreeNodeType> = (options: {
  itemId: TreeNodeId<T>
  item: CompletionsItemFragment
  parent: TreeNode<T>["parent"]
}) => TreeNode<T>

const getCompletionMeta = (item: CompletionsItemFragment) => {
  const { completion, name } = item

  let title, subtitle

  const { user } = completion
  const action = getCompletionAction(completion)

  title = `${capitalCase(action)}: ${name}`
  subtitle = user ? `${user.full_name}` : null

  return { title, subtitle, user }
}

export const getCompletionNodeFromItem: GetNodeFromItem<"completion"> = ({
  itemId,
  item,
  parent
}) => {
  const { needs_grading, is_late, is_complete, completion_date, checked_by } =
    item.completion!
  let tags: TreeNodeMetaTag[] = []
  if (needs_grading) tags.push("pending-review")
  if (is_late) tags.push("overdue")
  if (checked_by) tags.push("reviewed")
  if (is_complete) tags.push("completed")
  if (!is_complete) tags.push("incomplete")

  const completion: TreeNode<"completion"> = {
    id: itemId,
    type: "completion",
    user: item.completion.user,
    created: item.completion.completion_date
      ? new Date(item.completion.completion_date)
      : null,
    meta: {
      ...getCompletionMeta(item),
      avatar: item.completion.user?.profile?.image,
      // icon: is_complete ? "check" : "box",
      when: completion_date ? new Date(completion_date) : null,
      tags
    },
    data: item,
    parent,
    children: null
  }

  return completion
}

export const getPathNodeFromItem: GetNodeFromItem<"path"> = ({
  itemId,
  item,
  parent
}) => {
  const { resource, cohort } = item
  const path: TreeNode<"path"> = {
    id: itemId,
    type: "path",
    meta: {
      title: resource!.name,
      subtitle: cohort!.name,
      image: resource!.image
    },
    data: item,
    parent,
    children: new Map()
  }
  return path
}

export const getLessonNodeFromItem: GetNodeFromItem<"lesson"> = ({
  itemId,
  item,
  parent
}) => {
  const lesson: TreeNode<"lesson"> = {
    id: itemId,
    type: "lesson",
    meta: {
      title: item.parent?.name || ""
    },
    data: item,
    parent: parent,
    children: new Map()
  }
  return lesson
}

export const getStepNodeFromItem: GetNodeFromItem<"step"> = ({
  itemId,
  item,
  parent
}) => {
  const step: TreeNode<"step"> = {
    id: itemId,
    type: "step",
    meta: {
      title: item.name,
      icon: getStepVerbIcon(item.verb || "")
    },
    data: item,
    parent,
    children: new Map()
  }
  return step
}

export type CreatePathCompletionsTreeReturn = ReturnType<
  typeof createPathCompletionsTree
>

export const createPathCompletionsTree = (
  context: UseReviewableCompletionsReturn,
  items: CompletionsItemFragment[]
) => {
  let map: Map<string, TreeNodeAny> = new Map()

  const { meta, id } = context

  const tree: TreeNode<"root"> = {
    id: `root:${id}`,
    meta,
    type: "root",
    data: null,
    parent: null,
    children: new Map()
  }

  let pathNodes: TreeNodeMap<"path"> = new Map()
  let lessonNodes: TreeNodeMap<"lesson"> = new Map()
  let stepNodes: TreeNodeMap<"step"> = new Map()

  map.set(tree.id, tree)

  items.forEach((item) => {
    // Get/set path node.
    const pathId: TreeNodeId<"path"> = `${tree.id}-path:${item.cohort?.id}`
    const path =
      pathNodes.get(pathId) ||
      getPathNodeFromItem({ itemId: pathId, item, parent: tree })
    pathNodes.set(pathId, path)
    tree.children.set(pathId, path)
    map.set(pathId, path)

    // Get/set lesson node.
    const lessonId: TreeNodeId<"lesson"> = `${path.id}-lesson:${item.parent_source_id}`
    const lesson =
      lessonNodes.get(lessonId) ||
      getLessonNodeFromItem({ itemId: lessonId, item, parent: path })
    lessonNodes.set(lessonId, lesson)
    path.children.set(lessonId, lesson)
    map.set(lessonId, lesson)

    // Get/set step node.
    const stepId: TreeNodeId<"step"> = `${lesson.id}-step:${item.source_id}`
    const step =
      stepNodes.get(stepId) ||
      getStepNodeFromItem({ itemId: stepId, item, parent: lesson })
    stepNodes.set(stepId, step)
    lesson.children.set(stepId, step)
    map.set(stepId, step)

    // Get/set completion node.
    const completionId: TreeNodeId<"completion"> = `${step.id}-completion:${item.id}`
    const completion = getCompletionNodeFromItem({
      itemId: completionId,
      item,
      parent: step
    })
    step.children.set(completionId, completion)
    map.set(completionId, completion)
  })

  return { map, tree }
}

export const flattenTreeToItem = (
  tree: TreeNode<"root">,
  itemId: TreeNodeAnyId
) => {
  let path: TreeNodeAny[] = []
  let root: TreeNodeAny | null = null
  let stack: TreeNodeAny[] = [tree]
  while (stack.length > 0) {
    let node = stack.pop()
    if (node) {
      if (`${node.id}` === `${itemId}`) {
        root = node
        break
      } else {
        path.push(node)
      }
      if (node.children && node.children.size > 0) {
        stack = stack.concat([...node.children.values()])
      }
    }
  }
  return { root, path }
}

export const flattenTree = (tree: TreeNode<"root">) => {
  // convert the tree to a flat array of all nodes
  let nodes: TreeNodeAny[] = []
  let stack: TreeNodeAny[] = [tree]
  while (stack.length > 0) {
    let node = stack.pop()
    if (node) {
      nodes.push(node)
      if (node.children && node.children.size > 0) {
        stack = stack.concat([...node.children.values()])
      }
    }
  }
  return nodes
}

export const isLeaf = (node: TreeNodeAny) => {
  return !node.children || node.children.size === 0
}

export const getLeafNodes = (node: TreeNodeAny) => {
  // rewrite this to depth first search
  if (isLeaf(node)) return [node]
  let leafNodes: TreeNodeAny[] = []
  node.children?.forEach((child) => {
    leafNodes = leafNodes.concat(getLeafNodes(child))
  })
  return leafNodes
}
export const findFirstParentOfType = <T extends TreeNodeType>(
  node: TreeNodeAny,
  type: T
): TreeNode<T> | TreeNode<T>["parent"] | null => {
  if (node.type === type) return node as TreeNode<T>
  if (node.parent) return findFirstParentOfType(node.parent, type)
  return null
}

export const logTree = (tree: TreeNode<"root">) => {
  // recursively log the tree meta.title
  const logNode = (node: TreeNodeAny, depth: string) => {
    console.log(`${depth} ${node.meta.title}`)
    node.children?.forEach((child) => logNode(child, depth + "  "))
  }
  logNode(tree, "")
}

export const isDecendant = (
  node: TreeNodeAny,
  ancestor: TreeNodeAny
): boolean => {
  if (node.id === ancestor.id) return true
  if (node.parent) return isDecendant(node.parent, ancestor)
  return false
}

export const getPathParent = (item: TreeNodeAny): TreeNode<"path"> | null => {
  if (item.parent && item.parent.type === "path") {
    return item.parent as TreeNode<"path">
  } else if (item.parent) {
    return getPathParent(item.parent)
  }
  return null
}
